import { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { Toaster } from 'sonner';
import type { User } from '@shared/types';

import LoginPage from './pages/LoginPage';
import MainPage from './pages/MainPage';
import AdminPage from './pages/AdminPage';
import PendingApprovalPage from './pages/PendingApprovalPage';

const APP_VERSION = '1.0.0';

function App() {
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const initialLoadDone = useRef(false);

  useEffect(() => {
    // Check version and clear old localStorage if needed
    // BUT preserve Supabase auth tokens
    const storedVersion = localStorage.getItem('app-version');
    if (storedVersion !== APP_VERSION) {
      console.log('[App] Version mismatch, clearing localStorage (preserving auth)');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // Preserve app-version, storage-version, and any Supabase auth keys
        if (key && key !== 'app-version' && key !== 'storage-version' && !key.startsWith('sb-') && !key.includes('uncommonable-auth')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      localStorage.setItem('app-version', APP_VERSION);
    }

    console.log('[App] Starting session check...');
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[App] Got session:', session ? 'YES' : 'NO');
      setSession(session);
      
      if (session) {
        console.log('[App] Loading user for ID:', session.user.id);
        await loadUser(session.user.id);
      } else {
        console.log('[App] No session, setting loading to false');
        setLoading(false);
      }
      initialLoadDone.current = true;
    }).catch(error => {
      console.error('[App] Error in session check:', error);
      setLoading(false);
      initialLoadDone.current = true;
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[App] Auth state changed:', event, session ? 'has session' : 'no session');
      
      // Skip if this is the initial SIGNED_IN event (we already handled it above)
      if (event === 'SIGNED_IN' && !initialLoadDone.current) {
        console.log('[App] Skipping duplicate SIGNED_IN event');
        return;
      }
      
      setSession(session);
      
      if (event === 'SIGNED_OUT') {
        console.log('[App] User signed out, clearing user data');
        setUser(null);
        setLoading(false);
        navigate('/login');
      } else if (session && event !== 'TOKEN_REFRESHED' && event !== 'SIGNED_IN') {
        // Only reload user on USER_UPDATED or other events
        // Skip SIGNED_IN (just session validation) and TOKEN_REFRESHED
        console.log('[App] Auth changed, reloading user');
        await loadUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const loadUser = async (userId: string) => {
    try {
      console.log('[App] Fetching user from database...');
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('[App] User query completed');
      console.log('[App] User query result:', { data, error });

      if (error) {
        console.error('[App] Failed to load user:', error);
        // If user doesn't exist in DB yet, that's okay - might be first login
        if (error.code === 'PGRST116') {
          console.log('[App] User not found in database (might be first login)');
        }
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('[App] Setting user:', data);
      setUser(data);
      setLoading(false);
    } catch (error: any) {
      console.error('[App] Exception loading user:', error);
      setUser(null);
      setLoading(false);
    }
  };

  console.log('[App] Render - loading:', loading, 'session:', !!session, 'user:', !!user);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is logged in but not approved, show pending page
  if (session && user && !user.is_approved) {
    return (
      <>
        <PendingApprovalPage />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={!session ? <LoginPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="/"
          element={session && user ? <MainPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/admin"
          element={session && user && user.is_super_user ? <AdminPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="*"
          element={<Navigate to={session && user ? "/" : "/login"} replace />}
        />
      </Routes>
      <Toaster />
    </>
  );
}

export default App;
